import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ACCOUNTANT_LINKS,
  CANDIDATE_APPOINTMENTS_LINKS,
  RRHH_APPOINTMENTS_AND_ABSENTEE_LINKS,
  RRHH_RESULT_LINKS,
  COMPANY_ADMIN_LINKS,
  PACIENTE_PARTICULAR_LINKS,
  RRHH_ABSENTEE_LINKS,
  RRHH_APPOINTMENTS_LINKS,
  RRHH_LINKS,
  ADM_ABSENTEE_LINKS,
  ASESOR_LINKS,
  GESTIONES_COMERCIALES,
  CRM_TICKETS,
  LOGIN,
} from "../../../constants/routes";
import { PORTAL_TERMS_ALLOWED_ROUTES } from "../../../constants/portalTerms";
import { isPortalAccessBlocked } from "../../../hooks/useSyncMockBillingPolicy";
import { RootState } from "../../../store/combineReducer";
import { removeTokensLocalStorage } from "../../../utils/auth/getTokensLocalStorage";
import { Link, useLocation, useNavigate } from "react-router-dom"
import StyledWrapper from "./style";
import { dropDownItems } from "../NavBar/dropdownItems";
import CompanyDropdown from "../CompanyDropdown/CompanyDropdown";
import { Company, DropDownCompany } from "../NavBar/Navbar.type";
import { setProfileUser } from "../../../store/user/actions";
import LavorisService from "../../../services/LavorisService";
import {
  filterAbsenteeismMenuItems,
  filterCuentaCorrienteMenuItems,
  getDisplayClientCode,
  isAbsenteeismPath,
  isCuentaCorrientePath,
  isFranchiseTenant,
} from "../../../utils/franchise/franchiseAbsenteeismMenu";
import ClearCacheButton from "../ClearCacheButton";

interface SideBarButtonsProps {
  onMobileItemClick?: (item: any) => void;
  compact?: boolean;
}

export default function SideBarButtons({ onMobileItemClick, compact = false }: SideBarButtonsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  let dispatch = useDispatch();
  const { reduceSideBar } = useSelector((state: RootState) => state.app);
  const reduxStore = useSelector((state: RootState) => state.user);
  const franchiseState = useSelector((state: RootState) => state.franchise);
  const isFranchise = isFranchiseTenant(franchiseState);
  const hideAbsenteeismMenus = isFranchise;
  const hideCuentaCorrienteMenus = isFranchise;
  const chosenRole: string = reduxStore?.currentRole;
  const portalTermsAccepted = reduxStore?.portalTermsAccepted;
  const [buttonSelected, setButtonSelected] = useState("");
  const [profile, setProfile] = useState(reduxStore);

  
  const getMenuItemTitleAttr = (itemTitle: string) => {    
    if (itemTitle === "Empleados") return "Registrá aquí los datos completos de tus colaboradores para que estén siempre disponibles para tus gestiones. Completá los datos de manera correcta para evitar inconvenientes en la gestión médica.";
    if (itemTitle === "Postulantes") return "Registrá aquí los candidatos quienes necesiten únicamente exámenes de pre-ingreso";
    if (itemTitle === "Turnos") return "Solicitá turnos de exámenes para realizarse en nuestro centro clínico en Rodríguez Peña 59";
    if (itemTitle === "Roles de usuarios") return "Desde aquí el Usuario Administrador podrá crear los diferentes roles (personas) que gestionarán en el sistema según su tarea. Por ej: Selección, RRHH, Pagos, etc.";
    if (itemTitle === "Resultados") return "Consultá los resultados finales de las solicitudes realizadas. Usá el filtro de fechas para visualizar correctamente los resultados.";
    if (itemTitle === "Ausentismo") return "Solicitá turnos de control de ausentismo para empleados.";
    return undefined;
  };


  const dropDownItemsSideBar = dropDownItems.map(item => {
    if (item.title === "Cambiar de cuenta") {
      return {
        ...item,
        submenu: item.submenu.filter(sub => sub.title !== "Cerrar sesión")
      };
    }
    return item;
  });


  //List of Link Options
  const ROUTELINKS: any = {
    COMPANY_ADMIN: COMPANY_ADMIN_LINKS,
    PARTICULAR_CLIENT: PACIENTE_PARTICULAR_LINKS,
    RRHH: RRHH_LINKS,
    RRHH_APPOINTMENTS: RRHH_APPOINTMENTS_LINKS,
    CANDIDATE_APPOINTMENTS: CANDIDATE_APPOINTMENTS_LINKS,
    RRHH_APPOINTMENTS_AND_ABSENTEE: RRHH_APPOINTMENTS_AND_ABSENTEE_LINKS,
    RRHH_RESULT: RRHH_RESULT_LINKS,
    RRHH_ABSENTEE: RRHH_ABSENTEE_LINKS,
    ADM_ABSENTEE: ADM_ABSENTEE_LINKS,
    ACCOUNTANT: ACCOUNTANT_LINKS,
    ASESOR: ASESOR_LINKS,
  };

  //Set Chosen Links to Render
  const CHOSEN_ROLE_LINKS = ROUTELINKS[chosenRole]
    ? ROUTELINKS[chosenRole]
    : [];

  const mustFilterForPortalTerms =
    chosenRole === "COMPANY_ADMIN" && isPortalAccessBlocked(portalTermsAccepted);

  const VISIBLE_ROLE_LINKS = mustFilterForPortalTerms
    ? CHOSEN_ROLE_LINKS.filter((link: { linkTo: string; title: string }) =>
        PORTAL_TERMS_ALLOWED_ROUTES.includes(link.linkTo) ||
        link.title === "Cerrar Sesión",
      )
    : CHOSEN_ROLE_LINKS;

  const FRANCHISE_FILTERED_LINKS = filterCuentaCorrienteMenuItems(
    filterAbsenteeismMenuItems(VISIBLE_ROLE_LINKS, hideAbsenteeismMenus),
    hideCuentaCorrienteMenus,
  );

  const currentCompany = reduxStore?.currentCompany || reduxStore?.companies?.[0];
  const [showCrmTickets, setShowCrmTickets] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result =
          await LavorisService.getInstance().CrmTickets.hasVisibleTickets(
            currentCompany?.id,
          );
        if (!cancelled) setShowCrmTickets(Boolean(result?.hasTickets));
      } catch {
        if (!cancelled) setShowCrmTickets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentCompany?.id, chosenRole]);

  const menuLinks = useMemo(() => {
    if (!showCrmTickets || !FRANCHISE_FILTERED_LINKS.length) {
      return FRANCHISE_FILTERED_LINKS;
    }
    const ticketsItem = {
      imgSrc: "/images/Solicitudes.svg",
      title: "Comunicaciones",
      linkTo: CRM_TICKETS,
      available: true,
    };
    const already = FRANCHISE_FILTERED_LINKS.some(
      (l: any) => l.linkTo === CRM_TICKETS || l.title === "Comunicaciones",
    );
    if (already) return FRANCHISE_FILTERED_LINKS;
    const logoutIdx = FRANCHISE_FILTERED_LINKS.findIndex(
      (l: any) => l.title === "Cerrar Sesión" || l.linkTo === LOGIN,
    );
    if (logoutIdx < 0) return [...FRANCHISE_FILTERED_LINKS, ticketsItem];
    return [
      ...FRANCHISE_FILTERED_LINKS.slice(0, logoutIdx),
      ticketsItem,
      ...FRANCHISE_FILTERED_LINKS.slice(logoutIdx),
    ];
  }, [FRANCHISE_FILTERED_LINKS, showCrmTickets]);

  const logout = () => {
    removeTokensLocalStorage();
  };

  useEffect(() => {
    const inactivityTime = 20 * 60 * 1000; // tiempo de inactividad para cerrar sesión
    let timeout: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(logout, inactivityTime);
    };

    const handleActivity = () => {
      resetTimer();
    };

    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keypress", handleActivity);

    resetTimer();

    return () => {
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keypress", handleActivity);

      clearTimeout(timeout);
    };
  }, [logout]);

  useEffect(() => {
    if (
      chosenRole === "COMPANY_ADMIN" &&
      isPortalAccessBlocked(portalTermsAccepted) &&
      location.pathname === "/"
    ) {
      navigate(`${GESTIONES_COMERCIALES}?currentTab=condiciones`);
      return;
    }
    if (chosenRole === 'RRHH_APPOINTMENTS_AND_ABSENTEE' && location.pathname === '/') {
      navigate('/Turnos');
    }
    if (chosenRole === 'CANDIDATE_APPOINTMENTS' && location.pathname === '/') {
      navigate('/Turnos');
    }
    if (chosenRole === 'RRHH_RESULT' && location.pathname === '/') {
      navigate('/Resultados');
    }
    if (hideAbsenteeismMenus && isAbsenteeismPath(location.pathname)) {
      navigate('/');
      return;
    }
    if (hideCuentaCorrienteMenus && isCuentaCorrientePath(location.pathname)) {
      navigate('/');
    }
  }, [chosenRole, location.pathname, navigate, portalTermsAccepted, hideAbsenteeismMenus, hideCuentaCorrienteMenus]);

  const isCurrentRoute = (pathPattern: string) => {
    const path = location.pathname.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return path.includes(pathPattern.replace(/[^a-zA-Z0-9]/g, '').toLowerCase())
  }

  const assertSelected = (value: any) => {
    if (value.title === "Tablero" && location.pathname === "/") return true
    return value.title === buttonSelected && isCurrentRoute(value.title)
  }

  const getCodClient = () => {
    if (!profile) return "---";
    if (isFranchise) {
      if (profile.currentCompany) {
        return getDisplayClientCode(profile.currentCompany, true);
      }
      if (profile.companies?.length) {
        return getDisplayClientCode(profile.companies[0], true);
      }
      return "---";
    }
    if (profile.currentCompany?.managerId) {
      return profile.currentCompany.managerId;
    }
    if (profile.companies && profile.companies.length > 0) {
      const firstCompany = profile.companies[0];
      if (firstCompany && firstCompany.managerId) {
        return firstCompany.managerId;
      }
    }
    return "---";
  };

  const rolesDictionary: { [key: string]: string } = {
    'SUPER_ADMIN_LAVORIS': 'Super Administrador Lavoris',
    'ADMIN_LAVORIS': 'Administrador Lavoris',
    'COMPANY_ADMIN': 'Administrador de la Empresa',
    'COMPANY_ACCOUNTANT': 'Contador de la Empresa',
    'COMPANY_CANDIDATE': 'Candidato de Empresa',
    'COMPANY_EMPLOYEE': 'Empleado de Empresa',
    'PARTICULAR_CLIENT': 'Cliente Particular',
    'COMPANY_CONTACT': 'Empleado de Empresa',
    'ABSENTEE_DOCTOR': 'Doctor de ausentismo',
    'OCCUPATIONAL_DOCTOR': 'Doctor de medicina laboral',
    'PROFESSIONAL': 'Profesional',
    'RRHH': 'RRHH',
    'RRHH_APPOINTMENTS': 'RRHH turnos y resultado de turnos',
    'CANDIDATE_APPOINTMENTS': 'RRHH turnos Postulantes',
    'RRHH_APPOINTMENTS_AND_ABSENTEE': 'RRHH turnos S/Res',
    'RRHH_RESULT': 'RRHH Resultados',
    'RRHH_ABSENTEE': 'RRHH ausentismos y resultado de ausentismos',
    'ADM_ABSENTEE': 'ADM Ausentismo',
    'ACCOUNTANT': 'Contador',
    'ASESOR': 'Asesor',
  }

  //Helper Functions
  const handleChosenCompany = (item: DropDownCompany) => {
    let chosenProfile = profile?.companies?.find(
      (company: Company) => company.fantasyName === item.title,
    );
    //Check if dropdown click was on a Company. If so, update Redux
    if (chosenProfile) {
      let updatedProfile = profile;
      updatedProfile.currentCompany = chosenProfile;
      const setProfileRedux = async () => {
        try {
          let response = dispatch(setProfileUser(updatedProfile));
          let currentCompany: string | undefined =
            response.payload.currentCompany?.fantasyName;
          if (currentCompany === updatedProfile.currentCompany.fantasyName) {
            setTimeout(() => window.location.reload(), 500);
          }
        } catch (e) {
          console.log(e);
        }
      };
      setProfileRedux();
    } else {
      item.title === "Ver mi cuenta" && navigate(`/Cuenta/Datos-Empresa`);
    }
  };

  // DROPDOWN object, but excluding items with title "Cerrar Sesión"
  const DROPDOWN: any = {
    COMPANY_ADMIN: (
      <CompanyDropdown
        menuItems={dropDownItemsSideBar}
        handleChosenCompany={(item: DropDownCompany) =>
          handleChosenCompany(item)
        }
      />
    ),
    ASESOR: (
      <div className="asesor-no-profile">
        {/* No muestra perfil ni dropdown para ASESOR en mobile */}
      </div>
    ),
    PARTICULAR_CLIENT: (
      <Link to="/Cuenta">
        <img
          src="/images/Profile.svg"
          className="profile-image"
          alt="Profile"
        />
      </Link>
    ),
    RRHH: (
      <img
        src="/images/Profile.svg"
        className="profile-image"
        alt="Profile"
      />
    ),
  };

  return (
    <>

      <div className="sidebar-top">
        <div className="user-info d-block d-sm-none">
          <span className="welcome-text">
            Hola <strong>{chosenRole === "ASESOR" ? profile?.name : (profile?.companies && profile?.companies[0]?.socialReason || profile?.name || profile?.email)}</strong>
          </span>
          <div className="role-info">
            {rolesDictionary[chosenRole] && (
              <span className="role-text">
                {rolesDictionary[chosenRole]}
              </span>
            )}
            {chosenRole !== "PARTICULAR_CLIENT" && (
              <>
                <span className="divider"> | </span>
                <span className="client-code">
                  Cliente: #{getCodClient()}
                </span>
              </>
            )}
          </div>
        </div>

        <ul className="sidebar-buttons">
          {menuLinks.map((value: any, index: number) => {
            return value.title === "line" ? (
              <span key={value.title}>
                <img
                  src={
                    reduceSideBar
                      ? "/images/LineaCorta.svg"
                      : "/images/LineaLarga.svg"
                  }
                  alt=""
                />
              </span>
            ) : (
              <li
                key={value.title}
                onClick={async () => {
                  if (value.title === "Cerrar Sesión") {
                    await removeTokensLocalStorage();
                  }
                  if (onMobileItemClick) {
                    onMobileItemClick(value);
                  }
                }}
              >
                {value.available ? (
                  <>
                    <StyledWrapper.Link title={getMenuItemTitleAttr(value.title)}
                      selected={assertSelected(value)}
                      to={value.linkTo}
                      onClick={() => {
                        setButtonSelected(value.title);
                      }}
                    >
                      <img src={value.imgSrc} alt={value.title} />
                      {!reduceSideBar && (
                        <span>{value.title}</span>
                      )}
                    </StyledWrapper.Link>
                  </>
                ) : (
                  <StyledWrapper.LinkDisable title={getMenuItemTitleAttr(value.title)}>
                    <img src={value.imgSrc} alt={value.title} />
                    {!reduceSideBar && (
                      <span>
                        {value.title}
                      </span>
                    )}
                  </StyledWrapper.LinkDisable>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="sidebar-bottom">
        <ul className="d-flex d-sm-none">
          {DROPDOWN[chosenRole] ? DROPDOWN[chosenRole] : <> </>}
        </ul>
        <ClearCacheButton compact={compact} />
      </div>

    </>
  );
}
